import type { TFunction, i18n as I18n } from 'i18next';
import { publishErrorCopy } from '@unipostudio/shared-types';
import { PLATFORM_LIST } from '@/shared/components/platform-list';
import { readinessIssueText } from '@/shared/lib/readiness-text';
import { formatDate, formatMoney } from '@/shared/lib/intl';
import { fromApiRole } from '@/shared/services/team.service';

/** The billing notice kinds (the API's BillingNoticeKind) and their copy under billing.event. */
const BILLING_EVENT_KEY: Readonly<Record<string, string>> = {
  payment_failed: 'paymentFailed',
  plan_halted: 'planHalted',
  plan_changed: 'planChanged',
  plan_ended: 'planEnded',
  cancel_confirmed: 'cancelConfirmed',
  refund_declined: 'refundDeclined',
  plan_limits_paused: 'limitsPaused',
};

/** `data.i18n` on a notification: a key under `notifications.*` and its values. */
export interface NotificationI18n {
  key: string;
  params: Record<string, unknown>;
}

/** The notification's `data.i18n`, when the server wrote one under `notifications.*`. */
export function notificationI18n(data: Record<string, unknown> | null | undefined): NotificationI18n | null {
  const raw = data?.['i18n'];
  if (!raw || typeof raw !== 'object') return null;
  const { key, params } = raw as { key?: unknown; params?: unknown };
  if (typeof key !== 'string' || !key.startsWith('notifications.')) return null;
  return { key, params: params && typeof params === 'object' ? (params as Record<string, unknown>) : {} };
}

/** A platform as the server names it (INSTAGRAM, GOOGLE_BUSINESS) in the app's own words. */
function platformLabel(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  return PLATFORM_LIST.find((p) => p.id === value.toLowerCase())?.label ?? value;
}

/**
 * The values a key is rendered with. The platform is named as the app names
 * it; an escalation without a sender name says "a customer"; a count of
 * stopped channels or minutes drives the plural.
 */
function paramsFor(i18nData: NotificationI18n, t: TFunction): Record<string, unknown> {
  const params: Record<string, unknown> = { ...i18nData.params };
  if ('platform' in params) params['platform'] = platformLabel(params['platform']);
  if ('who' in params && (typeof params['who'] !== 'string' || params['who'].trim() === '')) {
    params['who'] = t('notifications.inbox.aCustomer');
  }
  // An invitation names the role as the API does (EDITOR, CLIENT_VIEWER): worded as Settings → Team words it.
  if (typeof params['role'] === 'string') params['role'] = t(`settings.teamTab.roles.${fromApiRole(params['role'])}`);
  if (typeof params['stopped'] === 'number') params['count'] = params['stopped'];
  if (typeof params['minutes'] === 'number') params['count'] = params['minutes'];
  // A team role (EDITOR) in the reader's words.
  if (typeof params['role'] === 'string') params['role'] = t(`notifications.team.roles.${params['role'].toLowerCase()}`, { defaultValue: params['role'] });
  return params;
}

/**
 * Title and body in the reader's language when the server sent `data.i18n`
 * (`<key>.title`, `<key>.body`); anything the app has no words for falls back
 * to the stored text, which is English. Some notifications keep their stored
 * body by design: a provider's own error, or the customer's message.
 */
export function localizeNotification(
  n: { title: string; body: string; data?: Record<string, unknown> | null },
  t: TFunction,
  i18n: Pick<I18n, 'exists'>,
): { title: string; body: string } {
  const i18nData = notificationI18n(n.data);
  if (!i18nData) return { title: n.title, body: n.body };
  const params = paramsFor(i18nData, t);
  // A failed publish that carries its code reads as on every other screen
  // (PUBLISH_ERROR_COPY): the code's title and reason, never the channel's
  // English. Older notifications, stored without the code, keep their text.
  const errorCode = n.data?.['errorCode'];
  const rawPlatform = i18nData.params['platform'];
  if (i18nData.key === 'notifications.post.failed' && (typeof errorCode === 'string' || errorCode === null) && typeof rawPlatform === 'string') {
    const copy = publishErrorCopy(rawPlatform, errorCode);
    return { title: String(t(copy.titleKey, params)), body: String(t(copy.reasonKey, params)) };
  }
  // The weekly plan is ready (Task 2.2): how many posts.
  if (i18nData.key === 'notifications.plan.ready' && typeof i18nData.params['count'] === 'number') {
    return { title: String(t('planNotice.title', { count: i18nData.params['count'] })), body: String(t('planNotice.body')) };
  }
  // A client approved through a link after the post's time had passed (Task 5.1).
  if (i18nData.key === 'notifications.approval.needsTime') {
    return { title: String(t('notifications.approval.needsTimeTitle', params)), body: String(t('notifications.approval.needsTimeBody')) };
  }
  // The Monday report (Task 3.4): last week's enquiries and bookings.
  if (i18nData.key === 'notifications.weeklyReport.ready') {
    const title = i18nData.params['quiet'] === true ? t('weeklyReport.subjectQuiet') : t('weeklyReport.subject', params);
    return { title: String(title), body: String(t('weeklyReport.inApp')) };
  }
  // A refund made (Task 4.2): the amount, in the reader's format.
  if (i18nData.key === 'notifications.billing.refunded') {
    const minor = n.data?.['amountMinor'];
    const currency = n.data?.['currency'];
    if (typeof minor === 'number' && typeof currency === 'string') {
      const amount = formatMoney(minor / 100, currency);
      return { title: String(t('billing.notification.refundedTitle')), body: String(t('billing.notification.refundedBody', { amount })) };
    }
    return { title: n.title, body: n.body };
  }
  // Billing account notices (AL-11, AL-28, D-01): the words of the email,
  // billing.event.<kind>, with the plan named as the app names it.
  if (i18nData.key === 'notifications.billing.event' && typeof i18nData.params['kind'] === 'string') {
    const key = BILLING_EVENT_KEY[i18nData.params['kind']];
    if (key) {
      const planName = (v: unknown): string => (typeof v === 'string' && v ? String(t(`overview.plans.${v.toLowerCase()}`)) : '');
      const date = typeof i18nData.params['date'] === 'string' ? formatDate(i18nData.params['date'], { day: 'numeric', month: 'long', year: 'numeric' }) : '';
      const reason = typeof i18nData.params['reason'] === 'string' ? i18nData.params['reason'] : '';
      const values = { plan: planName(i18nData.params['plan']), from: planName(i18nData.params['from']), date, reason };
      const short = (key === 'refundDeclined' && !reason) || (key === 'cancelConfirmed' && !date);
      return {
        title: String(t(`billing.event.${key}.title`, values)),
        body: String(t(`billing.event.${key}.${short ? 'bodyShort' : 'body'}`, values)),
      };
    }
  }
  // Renewal notices (Task 4.1): the plan, the date and the amount with tax.
  // There is no trial (ADR-007): an old trial notice keeps the text it was stored with.
  if (i18nData.key === 'notifications.billing.renewal' && typeof i18nData.params['at'] === 'string') {
    const count = typeof i18nData.params['count'] === 'number' ? i18nData.params['count'] : 0;
    const plan = typeof i18nData.params['plan'] === 'string' ? t(`overview.plans.${i18nData.params['plan'].toLowerCase()}`) : '';
    const minor = n.data?.['amountMinor'];
    const currency = n.data?.['currency'];
    const amount = typeof minor === 'number' && typeof currency === 'string' ? formatMoney(minor / 100, currency) : null;
    const values = { plan, count, date: formatDate(i18nData.params['at'], { day: 'numeric', month: 'long', year: 'numeric' }), amount: amount ?? '' };
    return { title: String(t('billing.notification.renewalTitle', values)), body: String(t(amount ? 'billing.notification.renewalBody' : 'billing.notification.renewalBodyNoAmount', values)) };
  }
  // A reconnect warning (Task 1.4): the date, and the posts that would fail.
  if (i18nData.key === 'notifications.channel.reconnectBy' && typeof i18nData.params['at'] === 'string') {
    const count = typeof i18nData.params['count'] === 'number' ? i18nData.params['count'] : 0;
    const values = { platform: params['platform'], at: formatDate(i18nData.params['at'], { day: 'numeric', month: 'long' }), count };
    return {
      title: String(t('reconnect.notification.title', values)),
      body: String(t(count > 0 ? 'reconnect.notification.bodyPosts' : 'reconnect.notification.body', values)),
    };
  }
  // A post's receipt (Task 1.3): how many channels it went out on.
  if (i18nData.key === 'notifications.post.published' && typeof i18nData.params['count'] === 'number' && typeof i18nData.params['total'] === 'number') {
    const count = i18nData.params['count'];
    const total = i18nData.params['total'];
    return {
      title: String(count === total ? t('receipt.notification.title', { count }) : t('receipt.notification.titlePartial', { count, total })),
      body: String(t('receipt.notification.body')),
    };
  }
  if (i18nData.key === 'notifications.post.gone' && typeof i18nData.params['platform'] === 'string') {
    return {
      title: String(t('receipt.notification.goneTitle', { platform: params['platform'] })),
      body: String(t('receipt.notification.goneBody')),
    };
  }
  // A post at risk (Task 1.2) says how many posts, then the first one's issue.
  const issue = n.data?.['issue'];
  if (i18nData.key === 'notifications.post.atRisk' && issue && typeof issue === 'object' && typeof (issue as { messageKey?: unknown }).messageKey === 'string') {
    const count = typeof i18nData.params['count'] === 'number' ? i18nData.params['count'] : 1;
    const { messageKey, params: issueParams } = issue as { messageKey: string; params?: Record<string, string | number> };
    return {
      title: String(t('readiness.notification.title', { count })),
      body: readinessIssueText({ messageKey, params: issueParams ?? {} }, t),
    };
  }
  const has = (suffix: string): boolean =>
    i18n.exists(`${i18nData.key}.${suffix}`) ||
    // A plural body is stored as body_one / body_other.
    i18n.exists(`${i18nData.key}.${suffix}_other`);
  return {
    title: has('title') ? String(t(`${i18nData.key}.title`, params)) : n.title,
    body: has('body') ? String(t(`${i18nData.key}.body`, params)) : n.body,
  };
}
